import { logClientWsEvent } from './ws-wire-log';

export interface WsClient {
  /** Send a message. Buffered if not yet OPEN; flushed on first OPEN. */
  send(msg: unknown): void;
  /** Subscribe to incoming parsed JSON messages. Returns unsubscribe. */
  subscribe(handler: (msg: unknown) => void): () => void;
  /** Subscribe to connection state transitions: 'connecting' | 'open' | 'closing' | 'closed'. */
  onState(handler: (state: WsState) => void): () => void;
  /** Current state. */
  readonly state: WsState;
  /** Close the connection cleanly. Idempotent. */
  close(): void;
  /** Close the active socket without permanently shutting down the client. */
  disconnectTemporarily(reason?: string): void;
  /** Drop any pending socket/reconnect timer and open a fresh connection immediately. */
  reconnectNow(reason?: string): void;
  /** Test-only hook used by browser E2E to exercise the unexpected-close reconnect path. */
  __debugForceUnexpectedClose?(): void;
}

export type WsState = 'connecting' | 'open' | 'closing' | 'closed';

export interface WsCloseInfo {
  code: number;
  reason: string;
  wasClean: boolean;
}

export interface WsClientDeps {
  readonly url: string;
  /** Test injection point — defaults to `globalThis.WebSocket`. */
  readonly WebSocketCtor?: typeof WebSocket;
  /** Reconnect attempt limit. Default 5. After this, state stays 'closed'. */
  readonly maxReconnectAttempts?: number;
  /** Backoff schedule in ms: [200, 500, 1000, 2000, 4000]. Default same. */
  readonly backoffSchedule?: readonly number[];
  /** Test injection — defaults to `globalThis.setTimeout`. */
  readonly setTimeoutFn?: (cb: () => void, ms: number) => unknown;
  readonly clearTimeoutFn?: (id: unknown) => void;
  /** Time allowed for the browser WebSocket to reach OPEN. Default 8000ms. */
  readonly connectTimeoutMs?: number;
  readonly onClose?: (event: WsCloseInfo) => void;
  readonly shouldReconnect?: (event: WsCloseInfo, attempt: number) => boolean;
}

const DEFAULT_BACKOFF: readonly number[] = [200, 500, 1000, 2000, 4000];
const DEFAULT_MAX_RECONNECT = 5;
const DEFAULT_CONNECT_TIMEOUT_MS = 8_000;
const SESSION_RESTORED_ELSEWHERE_CLOSE_CODE = 4000;
const SESSION_RESTORED_ELSEWHERE_REASON = 'session_restored_elsewhere';
const CLIENT_CONNECT_TIMEOUT_CLOSE_CODE = 1006;
const CLIENT_CONNECT_TIMEOUT_REASON = 'connect_timeout';

export function createWsClient(deps: WsClientDeps): WsClient {
  const {
    url,
    WebSocketCtor = globalThis.WebSocket,
    maxReconnectAttempts = DEFAULT_MAX_RECONNECT,
    backoffSchedule = DEFAULT_BACKOFF,
    setTimeoutFn = (cb, ms) => setTimeout(cb, ms),
    clearTimeoutFn = (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
    connectTimeoutMs = DEFAULT_CONNECT_TIMEOUT_MS,
    onClose,
    shouldReconnect,
  } = deps;

  let currentState: WsState = 'closed';
  let ws: WebSocket | null = null;
  let reconnectAttempt = 0;
  let reconnectTimer: unknown = null;
  let connectTimer: unknown = null;
  let explicitlyClosed = false;

  const sendBuffer: unknown[] = [];
  const messageHandlers = new Set<(msg: unknown) => void>();
  const stateHandlers = new Set<(state: WsState) => void>();

  function sendJson(socket: WebSocket, msg: unknown, stage: string): boolean {
    if (socket.readyState !== WebSocketCtor.OPEN) {
      logClientWsEvent({
        direction: 'error',
        stage: 'send_socket_not_open',
        payload: msg,
        detail: `readyState=${socket.readyState}`,
      });
      return false;
    }

    const json = JSON.stringify(msg);
    try {
      socket.send(json);
    } catch (err: unknown) {
      logClientWsEvent({
        direction: 'error',
        stage: 'send_failed',
        payload: msg,
        detail: err instanceof Error ? err.message : String(err),
      });
      return false;
    }
    logClientWsEvent({ direction: 'out', stage, payload: msg });
    return true;
  }

  function setState(next: WsState): void {
    currentState = next;
    for (const handler of stateHandlers) {
      handler(next);
    }
  }

  function clearConnectTimer(): void {
    if (connectTimer === null) return;
    clearTimeoutFn(connectTimer);
    connectTimer = null;
  }

  function clearReconnectTimer(): void {
    if (reconnectTimer === null) return;
    clearTimeoutFn(reconnectTimer);
    reconnectTimer = null;
  }

  function detachSocketHandlers(socket: WebSocket): void {
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
  }

  function closeDetachedSocket(socket: WebSocket): void {
    detachSocketHandlers(socket);
    try {
      socket.close();
    } catch (err: unknown) {
      logClientWsEvent({
        direction: 'error',
        stage: 'socket_close_failed',
        detail: err instanceof Error ? err.message : String(err),
      });
    }
  }

  function scheduleReconnect(closeInfo: WsCloseInfo): void {
    const reconnectAllowedByPolicy = shouldReconnect?.(closeInfo, reconnectAttempt) ?? true;
    if (reconnectAllowedByPolicy && reconnectAttempt < maxReconnectAttempts) {
      const delay =
        backoffSchedule[reconnectAttempt] ?? backoffSchedule[backoffSchedule.length - 1];
      logClientWsEvent({
        direction: 'error',
        stage: 'reconnect_scheduled',
        detail: `attempt=${reconnectAttempt + 1} delayMs=${delay}`,
      });
      reconnectAttempt++;
      setState('closed');
      reconnectTimer = setTimeoutFn(() => {
        reconnectTimer = null;
        if (!explicitlyClosed) {
          connect();
        }
      }, delay);
    } else {
      setState('closed');
    }
  }

  function handleConnectTimeout(socket: WebSocket): void {
    if (ws !== socket || explicitlyClosed) return;
    ws = null;
    clearConnectTimer();
    closeDetachedSocket(socket);
    const closeInfo: WsCloseInfo = {
      code: CLIENT_CONNECT_TIMEOUT_CLOSE_CODE,
      reason: CLIENT_CONNECT_TIMEOUT_REASON,
      wasClean: false,
    };
    logClientWsEvent({
      direction: 'error',
      stage: 'connect_timeout',
      detail: `timeoutMs=${connectTimeoutMs}`,
    });
    onClose?.(closeInfo);
    scheduleReconnect(closeInfo);
  }

  function abandonActiveSocket(reason: string): void {
    clearConnectTimer();
    const socket = ws;
    ws = null;
    if (socket !== null) {
      logClientWsEvent({
        direction: 'error',
        stage: 'socket_abandoned',
        detail: reason,
      });
      closeDetachedSocket(socket);
    }
    if (currentState !== 'closed') {
      setState('closed');
    }
  }

  function connect(): void {
    logClientWsEvent({
      direction: 'error',
      stage: 'connect_start',
      detail: `attempt=${reconnectAttempt}`,
    });
    let socket: WebSocket;
    try {
      socket = new WebSocketCtor(url);
    } catch (err) {
      // Constructor can throw on invalid URL or browser restrictions.
      // Reset to 'closed' so ensureConnected() can retry on next send/subscribe.
      console.warn('[WsClient] WebSocket constructor threw — resetting to closed:', err);
      setState('closed');
      return;
    }
    // Assign ws BEFORE setState so that if an onState handler calls close()
    // synchronously during the 'connecting' transition, close() sees ws !== null
    // and calls socket.close() correctly (no zombie socket).
    ws = socket;
    setState('connecting');
    // Guard: if a synchronous onState handler called close(), clean up and abort.
    // We must call socket.close() here because onclose hasn't been attached yet
    // so the normal close path won't do it. Then manually reset to 'closed'.
    if (explicitlyClosed) {
      socket.close();
      ws = null;
      clearConnectTimer();
      // close() may have already set state to 'closing'; ensure we land at 'closed'
      if (currentState !== 'closed') {
        setState('closed');
      }
      return;
    }

    if (connectTimeoutMs > 0 && Number.isFinite(connectTimeoutMs)) {
      clearConnectTimer();
      connectTimer = setTimeoutFn(() => handleConnectTimeout(socket), connectTimeoutMs);
    }

    socket.onopen = () => {
      // Only process if this socket is still the active one and we haven't closed
      if (ws !== socket) return;
      clearConnectTimer();
      reconnectAttempt = 0;
      logClientWsEvent({ direction: 'error', stage: 'socket_open' });
      setState('open');
      // Flush buffered messages FIFO
      while (sendBuffer.length > 0) {
        const msg = sendBuffer.shift();
        if (!sendJson(socket, msg, 'flush_buffer')) {
          sendBuffer.unshift(msg);
          break;
        }
      }
    };

    socket.onmessage = (event: MessageEvent) => {
      if (ws !== socket) return;
      let parsed: unknown;
      try {
        parsed = JSON.parse(event.data as string);
      } catch {
        console.warn('[WsClient] Received malformed JSON — skipping:', event.data);
        logClientWsEvent({
          direction: 'error',
          stage: 'incoming_malformed_json',
          raw: event.data,
          detail: 'client JSON.parse failed',
        });
        return;
      }
      logClientWsEvent({ direction: 'in', stage: 'message', payload: parsed });
      for (const handler of messageHandlers) {
        handler(parsed);
      }
    };

    socket.onclose = (event) => {
      if (ws !== socket) return;
      clearConnectTimer();
      ws = null;
      const closeInfo: WsCloseInfo = {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      };
      logClientWsEvent({
        direction: 'error',
        stage: 'socket_close',
        detail: `code=${event.code} reason=${event.reason} wasClean=${event.wasClean}`,
      });
      onClose?.(closeInfo);

      // If the close was initiated by client.close(), don't reconnect
      if (explicitlyClosed) {
        setState('closed');
        return;
      }

      if (
        event?.code === SESSION_RESTORED_ELSEWHERE_CLOSE_CODE &&
        event.reason === SESSION_RESTORED_ELSEWHERE_REASON
      ) {
        explicitlyClosed = true;
        setState('closed');
        return;
      }

      // Reconnect on any unexpected close, including initial connection failures
      // (not just open→close transitions). This handles the case where the TCP
      // handshake succeeds but the WS upgrade fails before onopen fires.
      scheduleReconnect(closeInfo);
    };

    socket.onerror = () => {
      // onerror is always followed by onclose; rely on onclose to handle state
    };
  }

  function ensureConnected(): void {
    if (currentState === 'closed' && !explicitlyClosed && ws === null && reconnectTimer === null) {
      connect();
    }
  }

  const client: WsClient = {
    get state(): WsState {
      return currentState;
    },

    send(msg: unknown): void {
      if (currentState === 'open' && ws !== null && sendJson(ws, msg, 'send')) {
        return;
      }

      if (explicitlyClosed) return;

      logClientWsEvent({ direction: 'out', stage: 'queue', payload: msg });
      sendBuffer.push(msg);
      ensureConnected();
    },

    subscribe(handler: (msg: unknown) => void): () => void {
      // After explicit close, the client is permanently shut down. Don't add
      // the handler (it would leak forever since ensureConnected is gated by
      // !explicitlyClosed). Return a no-op unsubscribe.
      if (explicitlyClosed) {
        return () => {};
      }
      messageHandlers.add(handler);
      ensureConnected();
      return () => {
        messageHandlers.delete(handler);
      };
    },

    onState(handler: (state: WsState) => void): () => void {
      stateHandlers.add(handler);
      return () => {
        stateHandlers.delete(handler);
      };
    },

    close(): void {
      if (explicitlyClosed) return;
      explicitlyClosed = true;

      // Cancel any pending reconnect timer
      if (reconnectTimer !== null) {
        clearReconnectTimer();
      }
      clearConnectTimer();

      // Clear the send buffer
      sendBuffer.length = 0;

      if (ws !== null) {
        setState('closing');
        ws.close();
        // onclose will fire and transition to 'closed'
      } else if (currentState !== 'closed') {
        setState('closed');
      }
    },

    disconnectTemporarily(reason = 'temporary_disconnect'): void {
      if (explicitlyClosed) return;
      clearReconnectTimer();
      abandonActiveSocket(reason);
    },

    reconnectNow(reason = 'manual_reconnect'): void {
      if (explicitlyClosed) return;
      clearReconnectTimer();
      abandonActiveSocket(reason);
      connect();
    },

    __debugForceUnexpectedClose(): void {
      if (ws !== null) {
        ws.close(4001, 'e2e_forced_disconnect');
      }
    },
  };

  return client;
}
